/**
 * The project's default app: the service "Open app", the Preview gadget, the Apps "Default app" tag and a new share
 * link open first. Saved in the project as `.flow/project.json`, so every collaborator gets the same one and it travels
 * with the code:
 * - a service: `{ "defaultApp": "<service>" }` (short name);
 * - a file pinned in a service (a compose file or a Dockerfile, utils/runnablesModel, run as a session of its own):
 *   `{ "defaultApp": { "service": "app", "runnable": { "kind": "compose", "file": "docker-compose.yaml" } } }`, known
 *   here by its runner name (`app--compose-docker-compose-yaml`, the Apps item's short name) and its `runnable`
 *   (`{ service: 'services/app', kind, file }`, what Start sends), so Open app can start it while it does not run.
 * Unset (or a service that no longer exists): Flow's own guess as before (`app`, else the first frontend or the first
 * by name). One answer per project, shared by every caller.
 */
import * as vfsClient from './vfsClient';
import { RUNNABLE_KINDS, runnableServiceName } from './runnablesModel';

export const PROJECT_SETTINGS_PATH = '.flow/project.json';

const entries = new Map();
const entryOf = (projectId) => {
  if (!entries.has(projectId)) { entries.set(projectId, { name: null, runnable: null, loaded: false, loading: null, listeners: new Set() }); }
  return entries.get(projectId);
};
const set = (projectId, { name, runnable }) => {
  const entry = entryOf(projectId);
  entry.name = name;
  entry.runnable = runnable;
  entry.loaded = true;
  entry.listeners.forEach((listener) => listener(name, runnable));
};

const shortName = (name) => String(name || '').replace(/^services\//, '') || null;

/**
 * `{ name, runnable }` of a `defaultApp` value as saved: a short service name (`runnable` null), or a pinned file's
 * `{ service, runnable: { kind, file } }` (its runner name and `{ service: 'services/<service>', kind, file }`).
 * `{ name: null, runnable: null }` when unset or not understood.
 */
export function parseDefaultApp(value) {
  if (typeof value === 'string') { return { name: shortName(value), runnable: null }; }
  const service = shortName(value?.service);
  const kind = value?.runnable?.kind;
  const file = typeof value?.runnable?.file === 'string' ? value.runnable.file.trim() : '';
  if (!service) { return { name: null, runnable: null }; }
  if (!RUNNABLE_KINDS.includes(kind) || !file) { return { name: service, runnable: null }; }
  const runnable = { service: `services/${service}`, kind, file };
  return { name: shortName(runnableServiceName(runnable.service, runnable)), runnable };
}

/** The value saved for `name` (a short service name) or a pinned file (`runnable`: `{ service, kind, file }`). */
export function defaultAppValue(name, runnable = null) {
  if (runnable?.file && RUNNABLE_KINDS.includes(runnable.kind)) {
    return { service: shortName(runnable.service), runnable: { kind: runnable.kind, file: runnable.file } };
  }
  return shortName(name);
}

/** The default app known now (short name: a service's, or a pinned file's runner name), or null. */
export const defaultAppOf = (projectId) => (projectId ? entryOf(projectId).name : null);

/** The pinned file the default app is (`{ service, kind, file }`), or null when it is a service or unset. */
export const defaultRunnableOf = (projectId) => (projectId ? entryOf(projectId).runnable : null);

/** Calls `listener(name, runnable)` when the project's default app changes; returns the unsubscribe. */
export function subscribeDefaultApp(projectId, listener) {
  const entry = entryOf(projectId);
  entry.listeners.add(listener);
  return () => { entry.listeners.delete(listener); };
}

const parse = (text) => {
  try {
    const value = JSON.parse(text || '{}');
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  } catch {
    return null;
  }
};

/** Reads `.flow/project.json` once per project (again with `force`). Never rejects: no file means no default. */
export async function loadDefaultApp(projectId, { force = false } = {}) {
  if (!projectId) { return null; }
  const entry = entryOf(projectId);
  if (entry.loading) { return entry.loading; }
  if (entry.loaded && !force) { return entry.name; }
  entry.loading = Promise.resolve()
    .then(() => vfsClient.readFile(projectId, PROJECT_SETTINGS_PATH))
    .then((text) => { const value = parseDefaultApp(parse(text)?.defaultApp); set(projectId, value); return value.name; })
    .catch(() => { set(projectId, { name: entry.name, runnable: entry.runnable }); return entry.name; })
    .finally(() => { entry.loading = null; });
  return entry.loading;
}

/** The project's settings (`.flow/project.json`) as an object: {} when there is no file; null when it is not JSON. */
export async function readProjectSettings(projectId) {
  try {
    return parse(await vfsClient.readFile(projectId, PROJECT_SETTINGS_PATH));
  } catch {
    return {};
  }
}

/**
 * Changes the project's settings: `change(current)` returns the new object, written over the file with its other keys as
 * `change` kept them (the default app, the Apps tab's runnables, …). Refuses to overwrite a file that is not JSON.
 * Resolves to the new object; rejects when the file cannot be written.
 */
export async function updateProjectSettings(projectId, change) {
  const current = await readProjectSettings(projectId);
  if (current === null) { throw new Error(`${PROJECT_SETTINGS_PATH} is not valid JSON: fix it first.`); }
  const next = change({ ...current });
  await vfsClient.writeFile(projectId, PROJECT_SETTINGS_PATH, `${JSON.stringify(next, null, 2)}\n`);
  return next;
}

/**
 * Makes `service` the project's default app (null clears it), keeping the file's other keys; with `runnable`
 * (`{ service, kind, file }`) a file pinned in a service is the default app instead. Refuses to overwrite a file that is
 * not JSON. Resolves to the new name; rejects when the file cannot be written.
 */
export async function saveDefaultApp(projectId, service, { runnable = null } = {}) {
  const value = defaultAppValue(service, runnable);
  await updateProjectSettings(projectId, (next) => {
    if (value) { next.defaultApp = value; } else { delete next.defaultApp; }
    return next;
  });
  const parsed = value ? parseDefaultApp(value) : { name: null, runnable: null };
  set(projectId, parsed);
  return parsed.name;
}

/** Test helper. */
export function resetDefaultApps() { entries.clear(); }
